import "./SwipeableModals.scss";
import React, { useEffect, useState } from "react";
import SwipeableDrawer from "@mui/material/SwipeableDrawer";
import {
  FormControl,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
} from "@mui/material";

import AddPhoto from "./../../../assets/add-photo.png";
import BlueButton from "../../../components/BlueButton/BlueButton";
import { DataGrid } from "@mui/x-data-grid";
import DeleteUser from "./../../../assets/delete.svg";
import api from "../../../utils/api";
import { toast } from "react-toastify";

// Common styles for InputLabel to reduce repetition
const commonLabelStyles = {
  background: "white",
  transformOrigin: "right top",
  transform: "translate(14px, -9px) scale(0.75)",
  right: 24,
  left: "auto",
  paddingLeft: "4px",
  paddingRight: "4px",
};

const SwipeableModals = ({
  open,
  onClose,
  onOpen,
  currentView,
  initialData = null,
  refreshData,
}) => {
  const [image, setImage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [countries, setCountries] = useState([]);
  const [formData, setFormData] = useState({
    country_id: "",
    title: { ar: "", en: "" },
    content: { ar: "", en: "" },
    type: "", // 'web' or 'app'
  });
  const [pageData, setPageData] = useState({
    country_id: "",
    title_ar: "",
    title_en: "",
    desc_ar: "",
    desc_en: "",
    type: "app", // Default type
    image: null,
  });
  const isViewMode = currentView === "viewPage";
  const isEditMode = currentView === "editPage";
  const isCreateMode = currentView === "createPage";

  // Fetch countries on mount
  useEffect(() => {
    const getCountries = async () => {
      try {
        const res = await api.get("/countries");
        setCountries(res.data.data);
      } catch (error) {
        console.error(error);
      }
    };
    getCountries();
  }, []);

  // Set initial form data if editing
  useEffect(() => {
    if (
      initialData &&
      (currentView === "editTerm" || currentView === "viewTerm")
    ) {
      setFormData({
        country_id: initialData.country.id || "",
        title: {
          ar: initialData.title_ar || "",
          en: initialData.title_en || "",
        },
        content: {
          ar: initialData.content_ar || "",
          en: initialData.content_en || "",
        },
        type: initialData.type || "",
      });
    } else {
      // Determine type based on currentView for creation
      const type = currentView === "createWebTerm" ? "site" : "app";
      setFormData({
        country_id: "",
        title: { ar: "", en: "" },
        content: { ar: "", en: "" },
        type,
      });
    }
  }, [initialData, currentView]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      if (currentView === "createWebTerm" || currentView === "createAppTerm") {
        await api.post("/terms", formData);
        toast.success("تم إضافة الشروط بنجاح");
      } else if (currentView === "editTerm") {
        await api.put(`/terms/${initialData.id}`, formData);
        toast.success("تم تحديث الشروط بنجاح");
      }

      if (refreshData) {
        refreshData();
      }
      onClose();
    } catch (error) {
      toast.error(error.message || "حدث خطأ ما");
    } finally {
      setIsLoading(false);
    }
  };

  const CountryColumns = [
    {
      field: "ar.name",
      headerName: "اسم البنك بالعربية",
      minWidth: 160,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "en.name",
      headerName: "اسم البنك بالانجليزية",
      minWidth: 160,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "action",
      headerName: "",
      width: 60,
      headerAlign: "start",
      align: "start",
      sortable: false,
      filterable: false,
      flex: 1,
      renderCell: (params) => {
        return (
          <div className="cell-action" style={{ justifyContent: "flex-end" }}>
            <div
              className="cell-btn"
              onClick={() => {
                setOpenDelete(true);
              }}
            >
              <img src={DeleteUser} alt="" />
            </div>
          </div>
        );
      },
    },
  ];

  useEffect(() => {
    if ((isEditMode || isViewMode) && initialData) {
      // Set image from URL
      setImage(initialData.image || null);

      // Set other form data based on the actual structure
      setPageData({
        country_id: initialData.country_id || "",
        title_ar: initialData.title_ar || "",
        title_en: initialData.title_en || "",
        desc_ar: initialData.desc_ar || "",
        desc_en: initialData.desc_en || "",
        type: initialData.type || "app",
      });
    } else if (isCreateMode) {
      // Reset form for create mode
      setImage(null);
      setPageData({
        country_id: "",
        title_ar: "",
        title_en: "",
        desc_ar: "",
        desc_en: "",
        type: "app",
      });
    }
  }, [initialData, currentView]);

  const renderCreatePageView = () => {
    // Set appropriate title based on mode
    const pageTitleText = isViewMode
      ? "عرض صفحة"
      : isEditMode
      ? "تعديل صفحة"
      : "اضافة صفحة جديدة";

    // Local state for form data

    // Set form state based on initialData if in edit or view mode

    // Handle form submission
    const handleSubmit = async (e) => {
      e.preventDefault();

      // In view mode, just close the modal
      if (isViewMode) {
        onClose();
        return;
      }

      // For create and edit modes, prepare the data
      setIsLoading(true);

      try {
        // Create FormData for file upload
        const formDataToSend = new FormData();

        // Add text data with the correct field names
        formDataToSend.append("country_id", pageData.country_id);
        formDataToSend.append("title[ar]", pageData.title_ar);
        formDataToSend.append("title[en]", pageData.title_en);
        formDataToSend.append("desc[ar]", pageData.desc_ar);
        formDataToSend.append("desc[en]", pageData.desc_en);
        formDataToSend.append("type", pageData.type);
        formDataToSend.append("flag", "welcome_screen"); // Assuming this is needed based on your API

        // Add image if changed (for create always, for edit only if changed)
        if (image instanceof File) {
          formDataToSend.append("image", image);
        }

        // Different API calls based on create vs edit
        if (isCreateMode) {
          await api.post("/slides", formDataToSend, {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          });
          toast.success("تم إضافة الصفحة بنجاح");
        } else if (isEditMode) {
          await api.put(`/slides/${initialData.id}`, formDataToSend);
          toast.success("تم تحديث الصفحة بنجاح");
        }

        // Refresh data and close modal
        if (refreshData) {
          refreshData();
        }
        onClose();
      } catch (error) {
        toast.error(error.message || "حدث خطأ ما");
      } finally {
        setIsLoading(false);
      }
    };

    return (
      <div className="user-modal">
        <p className="user-modal-p">{pageTitleText}</p>
        <form onSubmit={handleSubmit}>
          <div className="form-img">
            <label
              htmlFor="image"
              className={`img-control ${isViewMode ? "disabled" : ""}`}
            >
              <img
                src={
                  image instanceof File
                    ? URL.createObjectURL(image)
                    : image
                    ? image
                    : AddPhoto
                }
                alt=""
              />
              {!isViewMode && <span>اضافة صورة</span>}
            </label>
            <input
              type="file"
              id="image"
              onChange={(e) => !isViewMode && setImage(e.target.files[0])}
              disabled={isViewMode}
            />
          </div>

          <FormControl fullWidth>
            <InputLabel htmlFor="country-select" shrink sx={commonLabelStyles}>
              اختر البلد
            </InputLabel>
            <Select
              labelId="country-select"
              id="country-select"
              value={pageData.country_id}
              onChange={(e) =>
                !isViewMode &&
                setPageData({ ...pageData, country_id: e.target.value })
              }
              displayEmpty={true}
              sx={{ borderRadius: "8px" }}
              disabled={isViewMode}
            >
              {countries.map((country) => (
                <MenuItem key={country.id} value={country.id}>
                  {country.name_ar}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl sx={{ width: "100%" }} variant="outlined">
            <InputLabel htmlFor="title-ar" shrink sx={commonLabelStyles}>
              عنوان النص بالعربية
            </InputLabel>
            <OutlinedInput
              sx={{ borderRadius: "8px" }}
              required
              id="title-ar"
              type="text"
              value={pageData.title_ar}
              onChange={(e) =>
                !isViewMode &&
                setPageData({
                  ...pageData,
                  title_ar: e.target.value,
                })
              }
              disabled={isViewMode}
            />
          </FormControl>

          <FormControl sx={{ width: "100%" }} variant="outlined">
            <InputLabel htmlFor="title-en" shrink sx={commonLabelStyles}>
              عنوان النص بالانجليزية
            </InputLabel>
            <OutlinedInput
              sx={{ borderRadius: "8px" }}
              required
              id="title-en"
              type="text"
              value={pageData.title_en}
              onChange={(e) =>
                !isViewMode &&
                setPageData({
                  ...pageData,
                  title_en: e.target.value,
                })
              }
              disabled={isViewMode}
            />
          </FormControl>

          <FormControl sx={{ width: "100%" }} variant="outlined">
            <InputLabel htmlFor="desc-ar" shrink sx={commonLabelStyles}>
              النص بالعربية
            </InputLabel>
            <OutlinedInput
              sx={{ borderRadius: "8px" }}
              required
              id="desc-ar"
              type="text"
              multiline
              minRows={5}
              value={pageData.desc_ar}
              onChange={(e) =>
                !isViewMode &&
                setPageData({
                  ...pageData,
                  desc_ar: e.target.value,
                })
              }
              disabled={isViewMode}
            />
          </FormControl>

          <FormControl sx={{ width: "100%" }} variant="outlined">
            <InputLabel htmlFor="desc-en" shrink sx={commonLabelStyles}>
              النص بالانجليزية
            </InputLabel>
            <OutlinedInput
              sx={{ borderRadius: "8px" }}
              required
              id="desc-en"
              type="text"
              multiline
              minRows={5}
              value={pageData.desc_en}
              onChange={(e) =>
                !isViewMode &&
                setPageData({
                  ...pageData,
                  desc_en: e.target.value,
                })
              }
              disabled={isViewMode}
            />
          </FormControl>

          <BlueButton
            text={isViewMode ? "إغلاق" : isEditMode ? "حفظ" : "اضافة"}
            size="medium"
            variant="fill"
            type={isViewMode ? "button" : "submit"}
            onClick={isViewMode ? onClose : undefined}
            loading={isLoading}
          />
        </form>
      </div>
    );
  };

  const renderTermForm = () => (
    <div className="user-modal">
      {currentView !== "viewTerm" && (
        <p className="user-modal-p">
          {currentView === "editTerm" ? "تعديل الشروط" : "اضافة شرط جديد"}
        </p>
      )}

      <form onSubmit={handleSubmit}>
        {/* Country Select */}
        <FormControl fullWidth>
          <InputLabel htmlFor="country-select" shrink sx={commonLabelStyles}>
            اختر البلد
          </InputLabel>
          <Select
            labelId="country-select"
            id="country-select"
            value={formData.country_id}
            onChange={(e) =>
              setFormData({ ...formData, country_id: e.target.value })
            }
            displayEmpty={true}
            sx={{ borderRadius: "8px" }}
            required
          >
            {countries.map((country) => (
              <MenuItem key={country.id} value={country.id}>
                {country.name_ar}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Title in Arabic */}
        <FormControl sx={{ width: "100%" }} variant="outlined">
          <InputLabel htmlFor="title-ar" shrink sx={commonLabelStyles}>
            عنوان النص بالعربية
          </InputLabel>
          <OutlinedInput
            sx={{ borderRadius: "8px" }}
            required
            id="title-ar"
            type="text"
            value={formData.title.ar}
            onChange={(e) =>
              setFormData({
                ...formData,
                title: { ...formData.title, ar: e.target.value },
              })
            }
          />
        </FormControl>

        {/* Title in English */}
        <FormControl sx={{ width: "100%" }} variant="outlined">
          <InputLabel htmlFor="title-en" shrink sx={commonLabelStyles}>
            عنوان النص بالانجليزية
          </InputLabel>
          <OutlinedInput
            sx={{ borderRadius: "8px" }}
            required
            id="title-en"
            type="text"
            value={formData.title.en}
            onChange={(e) =>
              setFormData({
                ...formData,
                title: { ...formData.title, en: e.target.value },
              })
            }
          />
        </FormControl>

        {/* Content in Arabic */}
        <FormControl sx={{ width: "100%" }} variant="outlined">
          <InputLabel htmlFor="content-ar" shrink sx={commonLabelStyles}>
            النص بالعربية
          </InputLabel>
          <OutlinedInput
            sx={{ borderRadius: "8px" }}
            required
            id="content-ar"
            type="text"
            multiline
            minRows={5}
            value={formData.content.ar}
            onChange={(e) =>
              setFormData({
                ...formData,
                content: { ...formData.content, ar: e.target.value },
              })
            }
          />
        </FormControl>

        {/* Content in English */}
        <FormControl sx={{ width: "100%" }} variant="outlined">
          <InputLabel htmlFor="content-en" shrink sx={commonLabelStyles}>
            النص بالانجليزية
          </InputLabel>
          <OutlinedInput
            sx={{ borderRadius: "8px" }}
            required
            id="content-en"
            type="text"
            multiline
            minRows={5}
            value={formData.content.en}
            onChange={(e) =>
              setFormData({
                ...formData,
                content: { ...formData.content, en: e.target.value },
              })
            }
          />
        </FormControl>

        {currentView !== "viewTerm" && (
          <BlueButton
            text={currentView === "editTerm" ? "تحديث" : "اضافة"}
            size="medium"
            variant="fill"
            loading={isLoading}
            type="submit"
          />
        )}
      </form>
    </div>
  );

  const renderCreateRegionView = () => (
    <div className="user-modal">
      <p className="user-modal-p">اضافة منطقة جديدة</p>
      <form>
        <FormControl sx={{ width: "100%" }} variant="outlined">
          <InputLabel
            htmlFor="outlined-adornment-password"
            shrink
            sx={{
              background: "white",
              transformOrigin: "right top",
              transform: "translate(14px, -9px) scale(0.75)",
              right: 24,
              left: "auto",
              paddingLeft: "4px",
              paddingRight: "4px",
            }}
          >
            اسم المنطقة بالعربية
          </InputLabel>
          <OutlinedInput
            sx={{ borderRadius: "8px" }}
            required
            id="outlined-adornment-password"
            type="text"
            value={""}
          />
        </FormControl>

        <FormControl sx={{ width: "100%" }} variant="outlined">
          <InputLabel
            htmlFor="outlined-adornment-password"
            shrink
            sx={{
              background: "white",
              transformOrigin: "right top",
              transform: "translate(14px, -9px) scale(0.75)",
              right: 24,
              left: "auto",
              paddingLeft: "4px",
              paddingRight: "4px",
            }}
          >
            اسم المنطقة بالانجليزية
          </InputLabel>
          <OutlinedInput
            sx={{ borderRadius: "8px" }}
            required
            id="outlined-adornment-password"
            type="text"
            value={""}
          />
        </FormControl>

        <FormControl fullWidth>
          <InputLabel
            htmlFor="outlined-adornment-password"
            shrink
            sx={{
              background: "white",
              transformOrigin: "right top",
              transform: "translate(14px, -9px) scale(0.75)",
              right: 24,
              left: "auto",
              paddingLeft: "4px",
              paddingRight: "4px",
            }}
          >
            البلد
          </InputLabel>
          <Select
            labelId="demo-simple-select-label"
            id="demo-simple-select"
            displayEmpty={true}
            sx={{ borderRadius: "8px" }}
            defaultValue={10}
          >
            <MenuItem value={10}>مصر</MenuItem>
          </Select>
        </FormControl>

        <FormControl fullWidth>
          <InputLabel
            htmlFor="outlined-adornment-password"
            shrink
            sx={{
              background: "white",
              transformOrigin: "right top",
              transform: "translate(14px, -9px) scale(0.75)",
              right: 24,
              left: "auto",
              paddingLeft: "4px",
              paddingRight: "4px",
            }}
          >
            المدينة
          </InputLabel>
          <Select
            labelId="demo-simple-select-label"
            id="demo-simple-select"
            displayEmpty={true}
            sx={{ borderRadius: "8px" }}
            defaultValue={10}
          >
            <MenuItem value={10}>مصر</MenuItem>
          </Select>
        </FormControl>

        <BlueButton text="اضافة" size="medium" variant="fill" />
      </form>
    </div>
  );

  const renderCreateCityView = () => (
    <div className="user-modal">
      <p className="user-modal-p">اضافة مدينة جديدة</p>
      <form>
        <FormControl fullWidth>
          <InputLabel
            htmlFor="outlined-adornment-password"
            shrink
            sx={{
              background: "white",
              transformOrigin: "right top",
              transform: "translate(14px, -9px) scale(0.75)",
              right: 24,
              left: "auto",
              paddingLeft: "4px",
              paddingRight: "4px",
            }}
          >
            البلد
          </InputLabel>
          <Select
            labelId="demo-simple-select-label"
            id="demo-simple-select"
            displayEmpty={true}
            sx={{ borderRadius: "8px" }}
            defaultValue={10}
          >
            <MenuItem value={10}>مصر</MenuItem>
          </Select>
        </FormControl>

        <FormControl sx={{ width: "100%" }} variant="outlined">
          <InputLabel
            htmlFor="outlined-adornment-password"
            shrink
            sx={{
              background: "white",
              transformOrigin: "right top",
              transform: "translate(14px, -9px) scale(0.75)",
              right: 24,
              left: "auto",
              paddingLeft: "4px",
              paddingRight: "4px",
            }}
          >
            اسم المدينة بالعربية
          </InputLabel>
          <OutlinedInput
            sx={{ borderRadius: "8px" }}
            required
            id="outlined-adornment-password"
            type="text"
            value={""}
          />
        </FormControl>

        <FormControl sx={{ width: "100%" }} variant="outlined">
          <InputLabel
            htmlFor="outlined-adornment-password"
            shrink
            sx={{
              background: "white",
              transformOrigin: "right top",
              transform: "translate(14px, -9px) scale(0.75)",
              right: 24,
              left: "auto",
              paddingLeft: "4px",
              paddingRight: "4px",
            }}
          >
            اسم المدينة بالانجليزية
          </InputLabel>
          <OutlinedInput
            sx={{ borderRadius: "8px" }}
            required
            id="outlined-adornment-password"
            type="text"
            value={""}
          />
        </FormControl>

        <FormControl sx={{ width: "100%" }} variant="outlined">
          <InputLabel
            htmlFor="outlined-adornment-password"
            shrink
            sx={{
              background: "white",
              transformOrigin: "right top",
              transform: "translate(14px, -9px) scale(0.75)",
              right: 24,
              left: "auto",
              paddingLeft: "4px",
              paddingRight: "4px",
            }}
          >
            فيمة الشحن
          </InputLabel>
          <OutlinedInput
            sx={{ borderRadius: "8px" }}
            required
            id="outlined-adornment-password"
            type="text"
            value={""}
          />
        </FormControl>

        <BlueButton text="اضافة" size="medium" variant="fill" />
      </form>
    </div>
  );

  const renderCreateCountryView = () => (
    <div className="user-modal">
      <p className="user-modal-p">اضافة بلد جديدة</p>
      <form>
        <FormControl sx={{ width: "100%" }} variant="outlined">
          <InputLabel
            htmlFor="outlined-adornment-password"
            shrink
            sx={{
              background: "white",
              transformOrigin: "right top",
              transform: "translate(14px, -9px) scale(0.75)",
              right: 24,
              left: "auto",
              paddingLeft: "4px",
              paddingRight: "4px",
            }}
          >
            اسم البلد بالعربية
          </InputLabel>
          <OutlinedInput
            sx={{ borderRadius: "8px" }}
            required
            id="outlined-adornment-password"
            type="text"
            value={""}
          />
        </FormControl>

        <FormControl sx={{ width: "100%" }} variant="outlined">
          <InputLabel
            htmlFor="outlined-adornment-password"
            shrink
            sx={{
              background: "white",
              transformOrigin: "right top",
              transform: "translate(14px, -9px) scale(0.75)",
              right: 24,
              left: "auto",
              paddingLeft: "4px",
              paddingRight: "4px",
            }}
          >
            اسم البلد بالانجليزية
          </InputLabel>
          <OutlinedInput
            sx={{ borderRadius: "8px" }}
            required
            id="outlined-adornment-password"
            type="text"
            value={""}
          />
        </FormControl>

        <FormControl sx={{ width: "100%" }} variant="outlined">
          <InputLabel
            htmlFor="outlined-adornment-password"
            shrink
            sx={{
              background: "white",
              transformOrigin: "right top",
              transform: "translate(14px, -9px) scale(0.75)",
              right: 24,
              left: "auto",
              paddingLeft: "4px",
              paddingRight: "4px",
            }}
          >
            اسم العملة بالعربية
          </InputLabel>
          <OutlinedInput
            sx={{ borderRadius: "8px" }}
            required
            id="outlined-adornment-password"
            type="text"
            value={""}
          />
        </FormControl>

        <FormControl sx={{ width: "100%" }} variant="outlined">
          <InputLabel
            htmlFor="outlined-adornment-password"
            shrink
            sx={{
              background: "white",
              transformOrigin: "right top",
              transform: "translate(14px, -9px) scale(0.75)",
              right: 24,
              left: "auto",
              paddingLeft: "4px",
              paddingRight: "4px",
            }}
          >
            اسم العملة بالانجليزية
          </InputLabel>
          <OutlinedInput
            sx={{ borderRadius: "8px" }}
            required
            id="outlined-adornment-password"
            type="text"
            value={""}
          />
        </FormControl>

        <FormControl sx={{ width: "100%" }} variant="outlined">
          <InputLabel
            htmlFor="outlined-adornment-password"
            shrink
            sx={{
              background: "white",
              transformOrigin: "right top",
              transform: "translate(14px, -9px) scale(0.75)",
              right: 24,
              left: "auto",
              paddingLeft: "4px",
              paddingRight: "4px",
            }}
          >
            القيمة المضافة
          </InputLabel>
          <OutlinedInput
            sx={{ borderRadius: "8px" }}
            required
            id="outlined-adornment-password"
            type="text"
            value={""}
          />
        </FormControl>

        <BlueButton text="اضافة" size="medium" variant="fill" />
      </form>
    </div>
  );

  const renderEditCountryView = () => (
    <div className="user-modal">
      <p className="user-modal-p">تعديل البلد</p>
      <form>
        <FormControl sx={{ width: "100%" }} variant="outlined">
          <InputLabel
            htmlFor="outlined-adornment-password"
            shrink
            sx={{
              background: "white",
              transformOrigin: "right top",
              transform: "translate(14px, -9px) scale(0.75)",
              right: 24,
              left: "auto",
              paddingLeft: "4px",
              paddingRight: "4px",
            }}
          >
            اسم البلد بالعربية
          </InputLabel>
          <OutlinedInput
            sx={{ borderRadius: "8px" }}
            required
            id="outlined-adornment-password"
            type="text"
            value={""}
          />
        </FormControl>

        <FormControl sx={{ width: "100%" }} variant="outlined">
          <InputLabel
            htmlFor="outlined-adornment-password"
            shrink
            sx={{
              background: "white",
              transformOrigin: "right top",
              transform: "translate(14px, -9px) scale(0.75)",
              right: 24,
              left: "auto",
              paddingLeft: "4px",
              paddingRight: "4px",
            }}
          >
            اسم البلد بالانجليزية
          </InputLabel>
          <OutlinedInput
            sx={{ borderRadius: "8px" }}
            required
            id="outlined-adornment-password"
            type="text"
            value={""}
          />
        </FormControl>

        <FormControl sx={{ width: "100%" }} variant="outlined">
          <InputLabel
            htmlFor="outlined-adornment-password"
            shrink
            sx={{
              background: "white",
              transformOrigin: "right top",
              transform: "translate(14px, -9px) scale(0.75)",
              right: 24,
              left: "auto",
              paddingLeft: "4px",
              paddingRight: "4px",
            }}
          >
            اسم العملة بالعربية
          </InputLabel>
          <OutlinedInput
            sx={{ borderRadius: "8px" }}
            required
            id="outlined-adornment-password"
            type="text"
            value={""}
          />
        </FormControl>

        <FormControl sx={{ width: "100%" }} variant="outlined">
          <InputLabel
            htmlFor="outlined-adornment-password"
            shrink
            sx={{
              background: "white",
              transformOrigin: "right top",
              transform: "translate(14px, -9px) scale(0.75)",
              right: 24,
              left: "auto",
              paddingLeft: "4px",
              paddingRight: "4px",
            }}
          >
            اسم العملة بالانجليزية
          </InputLabel>
          <OutlinedInput
            sx={{ borderRadius: "8px" }}
            required
            id="outlined-adornment-password"
            type="text"
            value={""}
          />
        </FormControl>

        <FormControl sx={{ width: "100%" }} variant="outlined">
          <InputLabel
            htmlFor="outlined-adornment-password"
            shrink
            sx={{
              background: "white",
              transformOrigin: "right top",
              transform: "translate(14px, -9px) scale(0.75)",
              right: 24,
              left: "auto",
              paddingLeft: "4px",
              paddingRight: "4px",
            }}
          >
            القيمة المضافة
          </InputLabel>
          <OutlinedInput
            sx={{ borderRadius: "8px" }}
            required
            id="outlined-adornment-password"
            type="text"
            value={""}
          />
        </FormControl>

        <BlueButton text="حفظ" size="medium" variant="fill" />
      </form>
    </div>
  );

  const renderCreateUnitView = () => (
    <div className="user-modal">
      <p className="user-modal-p">اضافة وحدة جديد</p>
      <form>
        <FormControl sx={{ width: "100%" }} variant="outlined">
          <InputLabel
            htmlFor="outlined-adornment-password"
            shrink
            sx={{
              background: "white",
              transformOrigin: "right top",
              transform: "translate(14px, -9px) scale(0.75)",
              right: 24,
              left: "auto",
              paddingLeft: "4px",
              paddingRight: "4px",
            }}
          >
            اسم الوحدة بالعربية
          </InputLabel>
          <OutlinedInput
            sx={{ borderRadius: "8px" }}
            required
            id="outlined-adornment-password"
            type="text"
            value={""}
          />
        </FormControl>

        <FormControl sx={{ width: "100%" }} variant="outlined">
          <InputLabel
            htmlFor="outlined-adornment-password"
            shrink
            sx={{
              background: "white",
              transformOrigin: "right top",
              transform: "translate(14px, -9px) scale(0.75)",
              right: 24,
              left: "auto",
              paddingLeft: "4px",
              paddingRight: "4px",
            }}
          >
            اسم الوحدة بالانجليزية
          </InputLabel>
          <OutlinedInput
            sx={{ borderRadius: "8px" }}
            required
            id="outlined-adornment-password"
            type="text"
            value={""}
          />
        </FormControl>
        <BlueButton text="اضافة" size="medium" variant="fill" />
      </form>
    </div>
  );

  const renderViewBankView = () => (
    <div className="user-modal">
      <p className="user-modal-p">البنوك</p>
      <DataGrid
        sx={{ backgroundColor: "white", width: "100%" }}
        autoHeight={true}
        columns={CountryColumns}
        rows={[]}
        disableVirtualization
        disableColumnFilter
        disableColumnMenu
        hideFooter
        rowHeight={60}
      />
    </div>
  );

  const renderCreateBankView = () => (
    <div className="user-modal">
      <p className="user-modal-p">اضافة بنك</p>
      <form>
        <FormControl sx={{ width: "100%" }} variant="outlined">
          <InputLabel
            htmlFor="outlined-adornment-password"
            shrink
            sx={{
              background: "white",
              transformOrigin: "right top",
              transform: "translate(14px, -9px) scale(0.75)",
              right: 24,
              left: "auto",
              paddingLeft: "4px",
              paddingRight: "4px",
            }}
          >
            اسم البنك بالعربية
          </InputLabel>
          <OutlinedInput
            sx={{ borderRadius: "8px" }}
            required
            id="outlined-adornment-password"
            type="text"
            value={""}
          />
        </FormControl>

        <FormControl sx={{ width: "100%" }} variant="outlined">
          <InputLabel
            htmlFor="outlined-adornment-password"
            shrink
            sx={{
              background: "white",
              transformOrigin: "right top",
              transform: "translate(14px, -9px) scale(0.75)",
              right: 24,
              left: "auto",
              paddingLeft: "4px",
              paddingRight: "4px",
            }}
          >
            اسم البنك بالانجليزية
          </InputLabel>
          <OutlinedInput
            sx={{ borderRadius: "8px" }}
            required
            id="outlined-adornment-password"
            type="text"
            value={""}
          />
        </FormControl>
        <BlueButton text="اضافة" size="medium" variant="fill" />
      </form>
    </div>
  );

  const renderDefaultView = () => <div></div>;

  const views = {
    createUnit: renderCreateUnitView,
    createCountry: renderCreateCountryView,
    editCountry: renderEditCountryView,
    createCity: renderCreateCityView,
    createRegion: renderCreateRegionView,
    createWebTerm: renderTermForm,
    createAppTerm: renderTermForm,
    editTerm: renderTermForm,
    viewTerm: renderTermForm,
    createPage: renderCreatePageView,
    editPage: renderCreatePageView,
    viewPage: renderCreatePageView,
    createBank: renderCreateBankView,
    viewBank: renderViewBankView,
  };

  return (
    <SwipeableDrawer
      anchor="left"
      open={open}
      onClose={onClose}
      onOpen={onOpen}
      sx={{ zIndex: "1202" }}
      disableScrollLock
    >
      {(views[currentView] || renderDefaultView)()}
    </SwipeableDrawer>
  );
};

export default SwipeableModals;
